import type {
  DocumentReference,
  Questionnaire,
  QuestionnaireResponse,
  Task,
} from '@medplum/fhirtypes';

import {
  MISSING_NOTE_TASK_CODE,
  SUPERVISION_TASK_SYSTEM,
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_DOCUMENT_COMPLETION_CODE,
  CLIENT_FACING_TASK_TAG_CODE,
  CLIENT_FACING_TASK_TAG_SYSTEM,
  CLIENT_FORM_ASSIGNMENT_CODE,
  CLIENT_SIGNATURE_REQUEST_CODE,
  CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  RULES_ENGINE_CODE_SYSTEM,
  RULES_ENGINE_FORM_ASSIGNMENT_CODE,
  TASK_TYPE_CODE_SYSTEM,
} from '../../../lib/taskFhirConstants';
import {
  isRecurringStandingOrder,
} from '@unify/fhir-model/resources/assessment-task';
import { APPOINTMENT_REQUEST_TASK } from './appointmentRequestTask';
import { APPOINTMENT_RESCHEDULE_TASK } from './rescheduleRequestTask';

// ---------------------------------------------------------------------------
// Tag / coding systems
// ---------------------------------------------------------------------------

/**
 * Tag system and code for documents explicitly shared to the client portal
 * by a provider. Used by FormsFilesTab share/unshare and isClientVisibleDocumentReference.
 */
export const CLIENT_VISIBLE_TAG_SYSTEM = 'https://unify.health/fhir/document-visibility';
export const CLIENT_VISIBLE_TAG_CODE = 'client-visible';

const CLINICAL_ASSESSMENT_CODES = new Set([
  'assessment',
  'screening',
  'risk-assessment',
  'clinical',
  'measure',
  'measures',
  'scored-measures',
  'diagnosis',
  'treatment-plan',
  'provider-note',
  'outcome-measure',
]);

const CLIENT_FACING_DOC_CODES = new Set([
  'consent',
  'intake-paperwork',
  'hipaa',
  'privacy-practices',
  'client-upload',
  'shared-client-document',
  'telehealth-consent',
  'informed-consent',
  'intake',
  'forms',
]);

/** Task-type codes assigned to clients (forms, assessments, signatures). */
const CLIENT_PORTAL_TASK_CODES = new Set([
  CLIENT_FORM_ASSIGNMENT_CODE,
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  CLIENT_SIGNATURE_REQUEST_CODE,
  CLIENT_DOCUMENT_COMPLETION_CODE,
]);

/** Provider-workflow task types that must never appear in the client portal. */
const PROVIDER_ONLY_TASK_CODES = new Set([
  'ad-hoc',
  'clinical-follow-up',
  MISSING_NOTE_TASK_CODE,
]);

const CLINICAL_TITLE_PATTERNS = [
  /\bphq[-\s]?9\b/i,
  /\bgad[-\s]?7\b/i,
  /\bpcl[-\s]?5\b/i,
  /\baudit[-\s]?c?\b/i,
  /\bdast[-\s]?\d+\b/i,
  /\bcage\b/i,
  /\baces\b/i,
  /\bbdi[-\s]?ii?\b/i,
  /\bburn\s*inventory\b/i,
  /\bcolumbia\b/i,
  /\bc[-\s]?ssrs\b/i,
  /\basq\b/i,
  /\bmdq\b/i,
  /depression\s+screen/i,
  /anxiety\s+screen/i,
  /risk\s+(assessment|screen)/i,
  /outcome\s+measure/i,
  /\bscreening\b/i,
  /\bsuicide\b/i,
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function codesFromResource(
  resource: { meta?: { tag?: { system?: string; code?: string }[] }; category?: { coding?: { system?: string; code?: string }[] }[] } | undefined,
): Set<string> {
  const codes = new Set<string>();
  if (!resource) return codes;

  for (const tag of resource.meta?.tag ?? []) {
    if (tag.code) codes.add(tag.code.toLowerCase());
  }
  for (const cat of (resource as { category?: { coding?: { system?: string; code?: string }[] }[] }).category ?? []) {
    for (const coding of cat.coding ?? []) {
      if (coding.code) codes.add(coding.code.toLowerCase());
    }
  }
  return codes;
}

function titleMatchesClinicalPattern(title: string | undefined): boolean {
  if (!title) return false;
  return CLINICAL_TITLE_PATTERNS.some((pat) => pat.test(title));
}

// ---------------------------------------------------------------------------
// Public classifiers
// ---------------------------------------------------------------------------

/**
 * True when the Questionnaire/QuestionnaireResponse represents a clinical
 * assessment (PHQ-9, GAD-7, risk tools, outcome measures, etc.) that must
 * NOT appear in the client-facing Documents tab.
 */
export function isClinicalQuestionnaire(
  q?: Questionnaire,
  qr?: QuestionnaireResponse,
): boolean {
  const codes = new Set([...codesFromResource(q), ...codesFromResource(qr)]);

  for (const code of codes) {
    if (CLINICAL_ASSESSMENT_CODES.has(code)) return true;
  }

  const title = q?.title ?? qr?.questionnaire;
  if (titleMatchesClinicalPattern(title)) return true;

  return false;
}

/**
 * True when the Questionnaire/QuestionnaireResponse is known client-facing
 * paperwork (consent, intake, HIPAA, forms, etc.) safe for the Documents tab.
 * Also returns true for any Questionnaire in the explicitly client-facing
 * `forms` category.
 */
export function isClientFacingDocumentQuestionnaire(
  q?: Questionnaire,
  qr?: QuestionnaireResponse,
): boolean {
  if (isClinicalQuestionnaire(q, qr)) return false;

  const codes = new Set([...codesFromResource(q), ...codesFromResource(qr)]);
  for (const code of codes) {
    if (CLIENT_FACING_DOC_CODES.has(code)) return true;
  }

  const title = (q?.title ?? '').toLowerCase();
  if (
    title.includes('consent') ||
    title.includes('hipaa') ||
    title.includes('privacy') ||
    title.includes('intake')
  ) {
    return true;
  }

  // If the Questionnaire has useContext indicating a client-facing document code,
  // treat it as client-facing.
  const useContextCodes = (q as { useContext?: { code?: { code?: string } }[] })?.useContext ?? [];
  for (const uc of useContextCodes) {
    const ucCode = uc.code?.code?.toLowerCase();
    if (ucCode && CLIENT_FACING_DOC_CODES.has(ucCode)) return true;
  }

  return false;
}

/**
 * True when a Task is specifically a Rules Engine form assignment.
 */
export function isRulesEngineFormAssignment(task: Task): boolean {
  const codings = task.code?.coding ?? [];
  return codings.some(
    (c) =>
      (c.system === RULES_ENGINE_CODE_SYSTEM && c.code === RULES_ENGINE_FORM_ASSIGNMENT_CODE) ||
      (c.system === TASK_TYPE_CODE_SYSTEM && c.code === CLIENT_FORM_ASSIGNMENT_CODE),
  );
}

/**
 * True when a DocumentReference was uploaded by the client themselves.
 */
export function isClientUploadedDocument(
  doc: DocumentReference,
  patientId: string,
): boolean {
  const authorRef = doc.author?.[0]?.reference;
  return authorRef === `Patient/${patientId}`;
}

/**
 * True when a DocumentReference should be visible in the client portal Documents tab.
 * Includes client-uploaded documents and explicitly shared documents.
 * Excludes provider notes, clinical-only docs, and internal documents.
 */
export function isClientVisibleDocumentReference(
  doc: DocumentReference,
): boolean {
  const codes = codesFromResource(doc);

  for (const code of codes) {
    if (CLINICAL_ASSESSMENT_CODES.has(code)) return false;
  }

  for (const code of codes) {
    if (CLIENT_FACING_DOC_CODES.has(code)) return true;
  }

  // Explicit provider-shared tag (set by FormsFilesTab share action)
  if (doc.meta?.tag?.some(
    (t) => t.system === CLIENT_VISIBLE_TAG_SYSTEM && t.code === CLIENT_VISIBLE_TAG_CODE,
  )) {
    return true;
  }

  if (doc.securityLabel?.some((sl) =>
    sl.coding?.some((c) => c.code === 'R' || c.code === 'V'),
  )) {
    return false;
  }

  const docStatus = doc.docStatus;
  if (docStatus === 'preliminary' || docStatus === 'entered-in-error') return false;

  return false;
}

/**
 * True when a Task is intended for the client portal (not internal/admin).
 * Explicitly includes Rules Engine form-assignment tasks and any task with
 * the client-facing tag.
 *
 * Recurring standing-order Tasks (provider-created schedule config with a
 * frequency extension) are excluded — clients see occurrence Tasks created
 * by the Recurring Assessment Bot, not the standing order itself.
 */
export function isClientFacingTask(task: Task): boolean {
  const codes = codesFromResource(task);

  const internalCodes = new Set([
    'internal',
    'admin',
    'transfer',
    'matching',
    'provider-only',
    'supervision',
  ]);

  for (const code of codes) {
    if (internalCodes.has(code)) return false;
  }

  const taskCodings = task.code?.coding ?? [];
  const isProviderPortalAppointmentTask = taskCodings.some(
    (c) =>
      (c.system === APPOINTMENT_REQUEST_TASK.system && c.code === APPOINTMENT_REQUEST_TASK.code)
      || (c.system === APPOINTMENT_RESCHEDULE_TASK.system && c.code === APPOINTMENT_RESCHEDULE_TASK.code),
  );
  if (isProviderPortalAppointmentTask) return false;

  const isMissingNoteTask = taskCodings.some(
    (c) => c.system === TASK_TYPE_SYSTEM && c.code === MISSING_NOTE_TASK_CODE,
  );
  if (isMissingNoteTask) return false;

  const isSupervisionTask = taskCodings.some((c) => c.system === SUPERVISION_TASK_SYSTEM);
  if (isSupervisionTask) return false;

  const isProviderOnlyTask = taskCodings.some(
    (c) =>
      (c.system === TASK_TYPE_SYSTEM || c.system === TASK_TYPE_CODE_SYSTEM)
      && c.code
      && PROVIDER_ONLY_TASK_CODES.has(c.code),
  );
  if (isProviderOnlyTask) return false;

  // Recurring standing orders are schedule-config Tasks, not client-visible assignments.
  // Clients see occurrence Tasks (created by Recurring Assessment Bot or immediate first send).
  if (isRecurringStandingOrder(task)) {
    return false;
  }

  // Explicit client-facing tag from Rules Engine or other producers
  const tags = task.meta?.tag ?? [];
  if (tags.some((t) => t.system === CLIENT_FACING_TASK_TAG_SYSTEM && t.code === CLIENT_FACING_TASK_TAG_CODE)) {
    return true;
  }

  // Rules Engine form assignment (with or without the client-facing tag — backwards compat)
  if (taskCodings.some(
    (c) =>
      (c.system === RULES_ENGINE_CODE_SYSTEM && c.code === RULES_ENGINE_FORM_ASSIGNMENT_CODE) ||
      (c.system === TASK_TYPE_CODE_SYSTEM && c.code === CLIENT_FORM_ASSIGNMENT_CODE),
  )) {
    return true;
  }

  const hasClientPortalTaskCode = taskCodings.some(
    (c) =>
      (c.system === TASK_TYPE_CODE_SYSTEM || c.system === TASK_TYPE_SYSTEM)
      && c.code
      && CLIENT_PORTAL_TASK_CODES.has(c.code),
  );
  if (hasClientPortalTaskCode) return true;

  // Provider-owned tasks (Tasks page, supervision, demo seed) are never client-visible
  // unless they already matched an explicit client-facing marker above.
  if (task.owner?.reference?.startsWith('Practitioner/')) {
    return false;
  }

  // Patient-owned tasks (rare; no practitioner owner).
  if (task.owner?.reference?.startsWith('Patient/')) {
    return true;
  }

  return false;
}

/**
 * Determine the focus resource type from a Task.focus reference.
 */
export function focusResourceType(task: Task): string | undefined {
  const ref = task.focus?.reference;
  if (!ref) return undefined;
  const [rt] = ref.split('/');
  return rt;
}
